import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface HelpContentData {
  faqs: FAQItem[];
  videoUrl?: string;
  videoTitle?: string;
  pageTitle?: string;
}

interface HelpContentState {
  data: HelpContentData | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Converts a route path to a page name for the help API
 * e.g., "/policies" -> "policies", "/auditcentre/audits" -> "auditcentre-audits"
 */
function routeToPageName(pathname: string): string {
  return pathname
    .replace(/^\//, '') // Remove leading slash
    .replace(/\//g, '-') // Replace slashes with dashes
    .replace(/-+$/, '') // Remove trailing dashes
    || 'dashboard'; // Default to dashboard if empty
}

/**
 * Hook to fetch dynamic help content based on the current route
 */
export function useHelpContent(): HelpContentState & { refetch: () => void } {
  const location = useLocation();
  const [state, setState] = useState<HelpContentState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const fetchHelpContent = useCallback(async () => {
    const pageName = routeToPageName(location.pathname);
    
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await deiterateAPI.request<{ statusCode: number; data: any }>(
        'GET',
        `/v1/help/page/${encodeURIComponent(pageName)}`,
        { backend: 'thalassa' }
      );
      
      if (response?.data) {
        // Transform the API response to our HelpContentData format
        const helpData = response.data;
        
        // Extract FAQs from the help data
        const faqs: FAQItem[] = [];
        const videoUrl = helpData.videoUrl || helpData['video.url'];
        const videoTitle = helpData.videoTitle || helpData['video.title'] || 'Help Video';
        const pageTitle = helpData.pageTitle || helpData['page.title']?.text || pageName;

        // Parse FAQ items from the help data
        // The API might return items like "faq.1.question", "faq.1.answer", etc.
        const faqKeys = Object.keys(helpData).filter(k => k.startsWith('faq.'));
        const faqNumbers = [...new Set(faqKeys.map(k => k.split('.')[1]))];
        
        faqNumbers.forEach(num => {
          const question = helpData[`faq.${num}.question`]?.text || helpData[`faq.${num}.question`];
          const answer = helpData[`faq.${num}.answer`]?.text || helpData[`faq.${num}.answer`];
          if (question && answer) {
            faqs.push({ question, answer });
          }
        });

        setState({
          data: { faqs, videoUrl, videoTitle, pageTitle },
          isLoading: false,
          error: null,
        });
      } else {
        // No help content for this page, use defaults
        setState({
          data: null,
          isLoading: false,
          error: null,
        });
      }
    } catch (error) {
      console.error('Error fetching help content:', error);
      setState({
        data: null,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Failed to fetch help content'),
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchHelpContent();
  }, [fetchHelpContent]);

  return { ...state, refetch: fetchHelpContent };
}

export default useHelpContent;
